import { useCallback, useState } from "react";
import { DockedChatIsland } from "@/zapi-frontend/shared/ui/ResponsiveChatPanel";
import { EditProjectChat } from "@/zapi-frontend/widgets/EditProjectChat";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { useCaseFrameChatDoc } from "@/zapi-frontend/stores/caseFrameChatDoc";
import { useDocuments } from "@/zapi-frontend/shared/hooks/useDocuments/useDocuments";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useChatUnreadIndicator } from "@/zapi-frontend/shared/hooks/useChatUnreadIndicator";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";

export function CaseFrameChatIsland() {
  const { selectedProject } = useProjectStore();
  const openSidebar = useProjectSidebarStore((s) => s.openSidebar);
  const { role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);

  const projectId = selectedProject ? String(selectedProject.id) : "";
  const { createDocument, createWebLink, refetchDocuments } = useDocuments(projectId);
  const [assistantMessageCount, setAssistantMessageCount] = useState<number | null>(null);
  const { hasUnread, markSeen } = useChatUnreadIndicator(assistantMessageCount ?? 0, {
    ready: assistantMessageCount !== null,
    firstVisitKey: projectId ? `project:${projectId}` : undefined,
    conversationKey: projectId
  });

  const handleDocumentUpdate = useCallback((content: string) => {
    useCaseFrameChatDoc.getState().applyChatUpdate?.(content);
    const { selectedProject: current, setSelectedProject } = useProjectStore.getState();
    if (current) setSelectedProject({ ...current, caseBriefMdContent: content });
  }, []);

  if (!selectedProject) return null;

  return (
    <DockedChatIsland testId="case-frame-chat-root" hasUnread={hasUnread} hideFab={!!openSidebar} onOpenChange={markSeen}>
      <EditProjectChat
        projectId={Number(selectedProject.id)}
        language={selectedProject.language || "english"}
        onDocumentUpdate={handleDocumentUpdate}
        onAssistantMessageCountChange={setAssistantMessageCount}
        readOnly={!hasEdit}
        onUploadFiles={async (files) => {
          for (const file of files) {
            await createDocument(file);
          }
          await refetchDocuments();
        }}
        onAddWebLink={async (url) => {
          await createWebLink(url);
          await refetchDocuments();
        }}
      />
    </DockedChatIsland>
  );
}
